import {
  GENERATION_IMPACT_LAYOUT_SCHEMA_VERSION,
  GENERATION_IMPACT_SED_CONTRACT_VERSION,
} from '../../types/generationImpact';
import type {
  GenerationImpactFootprintProjection,
  GenerationImpactLayoutPublication,
  GenerationImpactParcelBoundarySnapshot,
  GenerationImpactScenarioSnapshot,
  GenerationImpactSedAuthorityResolution,
  GenerationImpactSedAuthoritySnapshot,
} from '../../types/generationImpact';
import { validateGenerationImpactParcelBoundarySnapshot } from './boundaryAuthority';
import {
  generationImpactLayoutPublicationIntegrityReason,
  generationImpactScenarioIntegrityReason,
} from './integrity';

export interface GenerationImpactSedCarrier {
  generationImpactAuthoritySnapshot?: GenerationImpactSedAuthoritySnapshot | null;
}

const FOOTPRINT_FIELDS = [
  'plantAcres',
  'coolingAcres',
  'switchyardAcres',
  'ccsAcres',
  'compressionAcres',
  'totalAcres',
] as const satisfies readonly (keyof GenerationImpactFootprintProjection)[];

function sameFootprint(
  left: GenerationImpactFootprintProjection,
  right: GenerationImpactFootprintProjection,
): boolean {
  return FOOTPRINT_FIELDS.every((field) => (
    Number.isFinite(left[field])
    && Number.isFinite(right[field])
    && left[field] === right[field]
  ));
}

function invalid(reason: string): GenerationImpactSedAuthorityResolution {
  return { status: 'invalid', snapshot: null, reason };
}

/**
 * Resolve and structurally verify the immutable Generation & Impact basis on a
 * SED. This deliberately returns an explicit invalid state instead of silently
 * falling back to live Project Intent or live map layers: a Test Fit generated
 * from a mismatched authority tuple would look precise while mixing revisions.
 */
export function resolveGenerationImpactSedAuthority(
  carrier: GenerationImpactSedCarrier | null | undefined,
): GenerationImpactSedAuthorityResolution {
  const snapshot = carrier?.generationImpactAuthoritySnapshot;
  if (snapshot == null) return { status: 'absent', snapshot: null };
  if (snapshot.contractVersion !== GENERATION_IMPACT_SED_CONTRACT_VERSION
    || snapshot.provenance?.contractVersion !== GENERATION_IMPACT_SED_CONTRACT_VERSION) {
    return invalid('Unsupported Generation & Impact SED authority contract version');
  }
  if (typeof snapshot.capturedAt !== 'string' || !Number.isFinite(Date.parse(snapshot.capturedAt))) {
    return invalid('Generation & Impact SED authority is missing a valid capture timestamp');
  }

  const {
    scenarioSnapshot: scenario,
    layoutPublication: publication,
    parcelBoundary,
    provenance,
    footprint,
  } = snapshot;
  if (!scenario || !publication || !parcelBoundary || !provenance || !footprint) {
    return invalid('Generation & Impact SED authority is incomplete');
  }
  const scenarioIntegrityReason = generationImpactScenarioIntegrityReason(scenario);
  if (scenarioIntegrityReason) return invalid(scenarioIntegrityReason);
  const publicationIntegrityReason = generationImpactLayoutPublicationIntegrityReason(publication);
  if (publicationIntegrityReason) return invalid(publicationIntegrityReason);
  const boundaryInvalidReason = validateGenerationImpactParcelBoundarySnapshot(parcelBoundary);
  if (boundaryInvalidReason) return invalid(boundaryInvalidReason);
  if (publication.schemaVersion !== GENERATION_IMPACT_LAYOUT_SCHEMA_VERSION) {
    return invalid('Published layout uses an unsupported schema version');
  }
  if (!Number.isInteger(publication.version) || publication.version < 1) {
    return invalid('Published layout version must be a positive integer');
  }
  if (scenario.id !== provenance.scenarioId
    || scenario.inputHash !== provenance.inputHash
    || scenario.resultHash !== provenance.resultHash
    || scenario.result.modelVersion !== provenance.modelVersion) {
    return invalid('Promoted scenario does not match the SED provenance tuple');
  }
  if ((scenario.promotedAt ?? undefined) !== (provenance.promotedAt ?? undefined)) {
    return invalid('Promoted scenario timestamp does not match the SED provenance tuple');
  }
  if (publication.publicationId !== provenance.layoutPublicationId
    || publication.version !== provenance.layoutVersion
    || publication.layoutHash !== provenance.layoutHash
    || publication.publishedAt !== provenance.layoutPublishedAt) {
    return invalid('Layout publication does not match the SED provenance tuple');
  }
  if (parcelBoundary.schemaVersion !== provenance.parcelBoundarySchemaVersion
    || parcelBoundary.source !== provenance.parcelBoundarySource
    || parcelBoundary.boundaryHash !== provenance.parcelBoundaryHash
    || parcelBoundary.areaAcres !== provenance.parcelBoundaryAreaAcres) {
    return invalid('Frozen parcel boundary does not match the SED provenance tuple');
  }
  if (publication.scenarioId !== scenario.id
    || publication.scenarioUpdatedAt !== scenario.updatedAt
    || publication.inputHash !== scenario.inputHash
    || publication.resultHash !== scenario.resultHash
    || publication.modelVersion !== scenario.result.modelVersion) {
    return invalid('Published layout was not generated from the promoted scenario');
  }

  const publicationScenario = publication.scenarioSnapshot;
  if (!publicationScenario
    || publicationScenario.id !== scenario.id
    || publicationScenario.updatedAt !== scenario.updatedAt
    || publicationScenario.inputHash !== scenario.inputHash
    || publicationScenario.resultHash !== scenario.resultHash
    || publicationScenario.result?.modelVersion !== scenario.result.modelVersion
    || (publicationScenario.promotedAt ?? undefined) !== (scenario.promotedAt ?? undefined)
    || (publicationScenario.promotedBy ?? undefined) !== (scenario.promotedBy ?? undefined)) {
    return invalid('Layout publication scenario snapshot differs from the promoted scenario revision');
  }
  if (!sameFootprint(footprint, scenario.result.footprint)
    || !sameFootprint(footprint, publicationScenario.result.footprint)) {
    return invalid('Frozen footprint differs from the promoted or published scenario result');
  }

  return { status: 'valid', snapshot };
}

function cloneJson<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T;
}

/** Build a validated, detached authority snapshot for persistence on a SED. */
export function createGenerationImpactSedAuthoritySnapshot(
  scenario: GenerationImpactScenarioSnapshot,
  publication: GenerationImpactLayoutPublication,
  parcelBoundary: GenerationImpactParcelBoundarySnapshot,
  capturedAt: string,
): GenerationImpactSedAuthoritySnapshot {
  const footprint: GenerationImpactFootprintProjection = {
    plantAcres: scenario.result.footprint.plantAcres,
    coolingAcres: scenario.result.footprint.coolingAcres,
    switchyardAcres: scenario.result.footprint.switchyardAcres,
    ccsAcres: scenario.result.footprint.ccsAcres,
    compressionAcres: scenario.result.footprint.compressionAcres,
    totalAcres: scenario.result.footprint.totalAcres,
  };
  const snapshot = cloneJson<GenerationImpactSedAuthoritySnapshot>({
    contractVersion: GENERATION_IMPACT_SED_CONTRACT_VERSION,
    capturedAt,
    provenance: {
      contractVersion: GENERATION_IMPACT_SED_CONTRACT_VERSION,
      scenarioId: scenario.id,
      inputHash: scenario.inputHash,
      resultHash: scenario.resultHash,
      modelVersion: scenario.result.modelVersion,
      ...(scenario.promotedAt ? { promotedAt: scenario.promotedAt } : {}),
      layoutPublicationId: publication.publicationId,
      layoutVersion: publication.version,
      layoutHash: publication.layoutHash,
      layoutPublishedAt: publication.publishedAt,
      parcelBoundarySchemaVersion: parcelBoundary.schemaVersion,
      parcelBoundarySource: parcelBoundary.source,
      parcelBoundaryHash: parcelBoundary.boundaryHash,
      parcelBoundaryAreaAcres: parcelBoundary.areaAcres,
    },
    scenarioSnapshot: scenario,
    layoutPublication: publication,
    parcelBoundary,
    footprint,
  });
  const resolved = resolveGenerationImpactSedAuthority({ generationImpactAuthoritySnapshot: snapshot });
  if (resolved.status !== 'valid') {
    throw new Error(
      resolved.status === 'invalid'
        ? `Cannot freeze Generation & Impact into SED: ${resolved.reason}`
        : 'Cannot freeze Generation & Impact into SED: authority snapshot is absent',
    );
  }
  return snapshot;
}
